/**
 * PatientFrequencyPage — pagina dedicata "Pazienti ricorrenti".
 *
 * Allineata graficamente alla Dashboard (PageHeader blu + KPI tiles
 * pastello + cards bianche con bordo). Filtri "da-mese / a-mese" e
 * Streak minimo. Mostra TUTTI i pazienti (no cap).
 *
 * Sorgente: GET /api/dashboard/patient-frequency?from_ym=YYYY-MM&to_ym=YYYY-MM&min_streak=N
 *
 * Filtri lato server:
 *  - esclude eventi cancelled/noshow/invalid
 *  - esclude titoli "blocchi" (riunione, stop, ...)
 *  - esclude contatti TEAM (filtro batch GHL + heuristic "Dr./Dott./(bdd)")
 */
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import axios from "axios";
import { BarChart, Bar, ResponsiveContainer, Tooltip, XAxis } from "recharts";
import { Download, Calendar as CalendarIcon, Filter, MessageSquare, RefreshCw, Users, TrendingUp, Trophy, ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";
import PageHeader from "../components/PageHeader";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MONTH_IT_LONG = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
const MONTH_IT_SHORT = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];

function buildMonthOptions() {
  const now = new Date();
  const start = new Date(2020, 0, 1);
  const opts = [];
  let y = now.getFullYear();
  let m = now.getMonth();
  while (y > start.getFullYear() || (y === start.getFullYear() && m >= start.getMonth())) {
    opts.push({ ym: `${y}-${String(m + 1).padStart(2, "0")}`, label: `${MONTH_IT_LONG[m]} ${y}` });
    if (m === 0) { y -= 1; m = 11; } else { m -= 1; }
  }
  return opts;
}

function ymShortLabel(ym) {
  if (!ym) return "";
  const m = parseInt(ym.slice(5, 7), 10);
  const y = ym.slice(2, 4);
  return `${MONTH_IT_SHORT[m - 1]} '${y}`;
}

function streakBadgeColor(s) {
  if (s >= 12) return "bg-rose-100 text-rose-700 border-rose-300";
  if (s >= 8) return "bg-emerald-100 text-emerald-700 border-emerald-300";
  if (s >= 5) return "bg-sky-100 text-sky-700 border-sky-300";
  if (s >= 3) return "bg-indigo-100 text-indigo-700 border-indigo-300";
  return "bg-slate-100 text-slate-600 border-slate-300";
}

function defaultRange() {
  const now = new Date();
  const fromDate = new Date(now.getFullYear(), now.getMonth() - 5, 1);
  const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  return { from_ym: fmt(fromDate), to_ym: fmt(now) };
}

export default function PatientFrequencyPage() {
  const monthOptions = useMemo(() => buildMonthOptions(), []);
  const defaults = useMemo(() => defaultRange(), []);
  const [fromYm, setFromYm] = useState(defaults.from_ym);
  const [toYm, setToYm] = useState(defaults.to_ym);
  const [minStreak, setMinStreak] = useState(2);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const retryTimerRef = useRef(null);

  // Stato di ordinamento cliccando sulle colonne. Default = streak desc
  // (più "pazienti ricorrenti" in cima) → coincide con l'ordinamento
  // backend. Cliccando su una colonna alterna asc/desc; cliccando su
  // un'altra colonna parte da desc (numerici) o asc (alfabetico).
  const [sortKey, setSortKey] = useState("max_streak");
  const [sortDir, setSortDir] = useState("desc");

  const toggleSort = (key) => {
    if (key === sortKey) {
      setSortDir((d) => (d === "desc" ? "asc" : "desc"));
    } else {
      setSortKey(key);
      setSortDir(key === "name" ? "asc" : "desc");
    }
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data: resp } = await axios.get(`${API}/dashboard/patient-frequency`, {
        params: { from_ym: fromYm, to_ym: toYm, min_streak: minStreak },
        withCredentials: true,
      });
      setData(resp);
      if (resp && resp.team_filter_applied === false) {
        if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
        retryTimerRef.current = setTimeout(() => fetchData(), 30000);
      }
    } catch (err) {
      setError(err?.response?.data?.detail || "Impossibile caricare i dati");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [fromYm, toYm, minStreak]);

  useEffect(() => {
    fetchData();
    return () => { if (retryTimerRef.current) clearTimeout(retryTimerRef.current); };
  }, [fetchData]);

  const rangeInvalid = fromYm > toYm;
  const rawPatients = data?.patients || [];
  // Client-side sort: il backend già ordina (streak desc, total desc,
  // nome asc) ma l'utente può cliccare sulle colonne per cambiarlo.
  const patients = useMemo(() => {
    const arr = [...rawPatients];
    arr.sort((a, b) => {
      let cmp;
      if (sortKey === "name") {
        cmp = (a.name || "").localeCompare(b.name || "", "it", { sensitivity: "base" });
      } else if (sortKey === "max_streak") {
        cmp = (a.max_streak || 0) - (b.max_streak || 0);
        if (cmp === 0) cmp = (a.total || 0) - (b.total || 0);
      } else if (sortKey === "total") {
        cmp = (a.total || 0) - (b.total || 0);
        if (cmp === 0) cmp = (a.max_streak || 0) - (b.max_streak || 0);
      } else {
        cmp = 0;
      }
      return sortDir === "asc" ? cmp : -cmp;
    });
    return arr;
  }, [rawPatients, sortKey, sortDir]);
  const monthRanges = data?.month_ranges || [];
  const totalPatients = data?.total_patients || 0;
  const windowSize = data?.months || 0;
  const withFeedback = useMemo(() => patients.filter((p) => p.has_feedback).length, [patients]);
  const totalAppts = useMemo(() => patients.reduce((s, p) => s + (p.total || 0), 0), [patients]);

  const exportCsv = useCallback(() => {
    if (!patients.length) return;
    const headers = ["Rank", "Paziente", "Feedback", "Streak (mesi consecutivi)", "Totale appuntamenti", ...monthRanges.map((mr) => mr.ym)];
    const rows = patients.map((p, i) => [
      i + 1,
      `"${(p.name || "").replace(/"/g, '""')}"`,
      p.has_feedback ? "SI" : "",
      p.max_streak,
      p.total,
      ...monthRanges.map((mr) => p.monthly?.[mr.ym] || 0),
    ]);
    const csv = [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `pazienti-ricorrenti_${fromYm}_${toYm}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [patients, monthRanges, fromYm, toYm]);

  return (
    <div className="min-h-screen bg-gray-100 bg-grid-light pt-[calc(7px+3.5rem+13px)] pb-10 px-4 lg:px-[100px]">
      <div className="w-full">
        <PageHeader
          title="Pazienti Ricorrenti"
          subtitle="Andamento mese per mese — pazienti con appuntamenti in mesi consecutivi"
          actions={
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={exportCsv}
                disabled={!patients.length}
                data-testid="export-csv-btn"
                title="Scarica i risultati in CSV"
                className="inline-flex items-center gap-2 h-10 px-4 rounded-xl bg-white/10 hover:bg-white/20 border border-white/20 text-white text-sm transition-colors backdrop-blur-sm disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Download className="w-4 h-4" />
                <span className="hidden sm:inline">Scarica CSV</span>
              </button>
              <button
                type="button"
                onClick={() => fetchData()}
                data-testid="refresh-btn"
                title="Aggiorna ora"
                aria-label="Aggiorna"
                className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-white/10 hover:bg-white/20 border border-white/20 text-white transition-colors backdrop-blur-sm"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              </button>
            </div>
          }
        />
      </div>

      {/* Filter card overlapping the blue banner — coerente con Dashboard */}
      <div className="w-full mb-6 -mt-8 relative z-10">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-4 flex flex-col sm:flex-row gap-3 sm:items-end sm:justify-between">
          <div className="flex flex-col sm:flex-row gap-3 flex-1">
            <div className="flex-1 min-w-0">
              <label className="block text-[11px] font-semibold text-slate-500 mb-1 flex items-center gap-1">
                <CalendarIcon className="w-3 h-3" /> Dal mese
              </label>
              <select
                value={fromYm}
                onChange={(e) => setFromYm(e.target.value)}
                data-testid="from-ym-select"
                className="h-10 w-full text-sm border border-slate-200 rounded-lg px-3 bg-white focus:outline-none focus:border-blue-400"
              >
                {monthOptions.map((o) => <option key={o.ym} value={o.ym}>{o.label}</option>)}
              </select>
            </div>
            <div className="flex-1 min-w-0">
              <label className="block text-[11px] font-semibold text-slate-500 mb-1 flex items-center gap-1">
                <CalendarIcon className="w-3 h-3" /> Al mese
              </label>
              <select
                value={toYm}
                onChange={(e) => setToYm(e.target.value)}
                data-testid="to-ym-select"
                className="h-10 w-full text-sm border border-slate-200 rounded-lg px-3 bg-white focus:outline-none focus:border-blue-400"
              >
                {monthOptions.map((o) => <option key={o.ym} value={o.ym}>{o.label}</option>)}
              </select>
            </div>
            <div className="flex-1 min-w-0">
              <label className="block text-[11px] font-semibold text-slate-500 mb-1 flex items-center gap-1">
                <Filter className="w-3 h-3" /> Streak minimo
              </label>
              <select
                value={minStreak}
                onChange={(e) => setMinStreak(Number(e.target.value))}
                data-testid="min-streak-select"
                className="h-10 w-full text-sm border border-slate-200 rounded-lg px-3 bg-white focus:outline-none focus:border-blue-400"
              >
                {[1, 2, 3, 4, 5, 6, 8, 12].map((n) => (
                  <option key={n} value={n}>≥ {n} {n === 1 ? "mese" : "mesi"} consecutivi</option>
                ))}
              </select>
            </div>
          </div>
        </div>
        {rangeInvalid && (
          <div className="mt-3 bg-rose-50 border border-rose-200 rounded-xl p-3 text-sm text-rose-700">
            &quot;Dal mese&quot; deve essere precedente o uguale a &quot;Al mese&quot;
          </div>
        )}
      </div>

      {/* KPI tiles — stile Dashboard */}
      {!loading && data && !error && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="kpi-bar">
          <KpiTile
            label="Pazienti trovati"
            value={totalPatients.toLocaleString("it-IT")}
            sublabel={`streak ≥ ${minStreak}`}
            icon={<Users className="w-5 h-5" />}
            accent="blue"
            testid="kpi-patients"
          />
          <KpiTile
            label="Appuntamenti totali"
            value={totalAppts.toLocaleString("it-IT")}
            sublabel="nel periodo"
            icon={<TrendingUp className="w-5 h-5" />}
            accent="rose"
            testid="kpi-appts"
          />
          <KpiTile
            label="Finestra"
            value={`${windowSize} ${windowSize === 1 ? "mese" : "mesi"}`}
            sublabel={`${ymShortLabel(monthRanges[0]?.ym || fromYm)} → ${ymShortLabel(monthRanges[monthRanges.length - 1]?.ym || toYm)}`}
            icon={<CalendarIcon className="w-5 h-5" />}
            accent="emerald"
            testid="kpi-window"
          />
          <KpiTile
            label="Con feedback"
            value={withFeedback.toLocaleString("it-IT")}
            sublabel={totalPatients ? `${Math.round((withFeedback / totalPatients) * 100)}% del totale` : ""}
            icon={<MessageSquare className="w-5 h-5" />}
            accent="amber"
            testid="kpi-feedback"
          />
        </div>
      )}

      {error && (
        <div className="mb-3 bg-rose-50 border border-rose-200 rounded-xl p-3 text-sm text-rose-700" data-testid="error-msg">{error}</div>
      )}

      {/* Table card */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
            <Trophy className="w-4 h-4 text-amber-500" />
            Classifica pazienti per streak di mesi consecutivi
          </h3>
          {!loading && data && (
            <span className="text-[11px] text-slate-400">{patients.length.toLocaleString("it-IT")} righe</span>
          )}
        </div>

        {loading ? (
          <div className="h-64 flex items-center justify-center text-sm text-slate-400">Caricamento…</div>
        ) : patients.length === 0 ? (
          <div className="h-64 flex items-center justify-center text-sm text-slate-400" data-testid="empty-state">
            Nessun paziente trovato con almeno {minStreak} {minStreak === 1 ? "mese" : "mesi"} consecutivi nel range scelto.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="patient-table">
              <thead>
                <tr className="text-slate-500 border-b border-slate-200 text-[11px] uppercase tracking-wide">
                  <th className="text-left py-3 px-2 font-semibold w-12">#</th>
                  <th className="text-left py-3 px-2 font-semibold">
                    <SortHeader
                      label="Paziente"
                      active={sortKey === "name"}
                      dir={sortDir}
                      onClick={() => toggleSort("name")}
                      testid="sort-name"
                    />
                  </th>
                  <th className="text-center py-3 px-2 font-semibold w-28">
                    <SortHeader
                      label="Streak"
                      active={sortKey === "max_streak"}
                      dir={sortDir}
                      onClick={() => toggleSort("max_streak")}
                      align="center"
                      testid="sort-streak"
                    />
                  </th>
                  <th className="text-center py-3 px-2 font-semibold w-24">
                    <SortHeader
                      label="Tot."
                      active={sortKey === "total"}
                      dir={sortDir}
                      onClick={() => toggleSort("total")}
                      align="center"
                      testid="sort-total"
                    />
                  </th>
                  <th className="text-left py-3 px-2 font-semibold">Andamento mese per mese</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {patients.map((p, i) => (
                  <Row key={p.contactId} rank={i + 1} patient={p} monthRanges={monthRanges} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// Header cliccabile per ordinare la tabella. Mostra:
//  - freccia ↑/↓ se la colonna è quella attiva
//  - doppia freccia neutra (più pallida) altrimenti
// Cliccando alterna la direzione (vedi `toggleSort` nel parent).
function SortHeader({ label, active, dir, onClick, align = "left", testid }) {
  const Icon = active ? (dir === "asc" ? ArrowUp : ArrowDown) : ArrowUpDown;
  const justify = align === "center" ? "justify-center" : "justify-start";
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-1 ${justify} w-full text-[11px] uppercase tracking-wide font-semibold hover:text-slate-800 transition-colors ${active ? "text-indigo-700" : "text-slate-500"}`}
      title={`Ordina per ${label}${active ? ` (${dir === "asc" ? "crescente" : "decrescente"})` : ""}`}
    >
      <span>{label}</span>
      <Icon className={`w-3 h-3 ${active ? "opacity-100" : "opacity-40"}`} />
    </button>
  );
}

// KPI tile coerente con `MetricCard` della Dashboard.
function KpiTile({ label, value, sublabel, icon, accent, testid }) {
  const palette = {
    blue: "bg-indigo-50 text-indigo-700 border-indigo-100",
    rose: "bg-rose-50 text-rose-700 border-rose-100",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-100",
    amber: "bg-amber-50 text-amber-700 border-amber-100",
  }[accent] || "bg-slate-50 text-slate-700 border-slate-100";

  return (
    <div className={`rounded-2xl border ${palette} p-4 shadow-sm`} data-testid={testid}>
      <div className="flex items-start justify-between mb-2">
        <span className="text-[11px] uppercase tracking-wide font-semibold opacity-80">{label}</span>
        <div className="w-9 h-9 bg-white rounded-xl flex items-center justify-center shadow-sm opacity-90">{icon}</div>
      </div>
      <div className="text-2xl font-bold tabular-nums leading-tight">{value}</div>
      {sublabel && <div className="text-[11px] opacity-70 mt-1">{sublabel}</div>}
    </div>
  );
}

function Row({ rank, patient, monthRanges }) {
  const sparkData = useMemo(
    () => monthRanges.map((mr) => ({ ym: mr.ym, label: mr.ym.slice(5, 7), count: patient.monthly?.[mr.ym] || 0 })),
    [monthRanges, patient.monthly],
  );
  const showTicks = monthRanges.length <= 24;

  return (
    <tr className="hover:bg-slate-50 transition-colors" data-testid={`patient-row-${patient.contactId}`}>
      <td className="py-2 px-2 text-slate-400 tabular-nums">{rank}</td>
      <td className="py-2 px-2 font-medium text-slate-800 truncate max-w-[300px]">
        {patient.has_feedback && (
          <span
            className="inline-flex items-center gap-1 mr-2 px-1.5 py-0.5 rounded-md bg-emerald-100 text-emerald-700 border border-emerald-300 text-[10px] font-bold uppercase tracking-wide align-middle"
            title="Ha lasciato feedback"
            data-testid={`feedback-badge-${patient.contactId}`}
          >
            <MessageSquare className="w-3 h-3" />
            Feedback
          </span>
        )}
        {patient.name}
      </td>
      <td className="py-2 px-2 text-center">
        <span
          className={`inline-block px-2 py-0.5 rounded-md border text-xs font-bold tabular-nums ${streakBadgeColor(patient.max_streak)}`}
          title={`Massima sequenza di ${patient.max_streak} mesi consecutivi`}
        >
          {patient.max_streak}
        </span>
      </td>
      <td className="py-2 px-2 text-center font-semibold text-slate-700 tabular-nums">{patient.total}</td>
      <td className="py-2 px-2">
        <div style={{ width: "100%", height: showTicks ? 52 : 40, minWidth: 220 }}>
          <ResponsiveContainer>
            <BarChart data={sparkData} margin={{ top: 2, right: 4, bottom: 0, left: 0 }}>
              {showTicks && (
                <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fontSize: 9, fill: "#94a3b8" }} interval={0} />
              )}
              <Tooltip
                cursor={{ fill: "rgba(99,102,241,0.05)" }}
                contentStyle={{ fontSize: 11, borderRadius: 6, border: "1px solid #e2e8f0", padding: "2px 6px" }}
                labelFormatter={(_label, payload) => payload?.[0]?.payload?.ym || ""}
                formatter={(value) => [value, "appuntamenti"]}
              />
              <Bar dataKey="count" fill="#6366f1" radius={[2, 2, 0, 0]} isAnimationActive={false} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </td>
    </tr>
  );
}
